import { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import { isServerSide } from '@mythical/ui'

interface TrackedScrollPosition {
  [key: string]: {
    position: number
    timestamp: number
  }
}

const SCROLL_POSITIONS_KEY = 'scrollPositions'
const MAX_TRACKED_PATHS = 2

const getScrollContainer = () => {
  if (isServerSide) return null
  // We use different scroll containers for mobile and desktop
  if (window.matchMedia('(max-width: 1020px)').matches) {
    return window
  } else {
    return document.getElementById('scroll-container')
  }
}

const getScrollPosition = (container?: Window | HTMLElement | null) => {
  if (!container) return 0
  if (container instanceof Window) {
    return container.scrollY
  }
  return container.scrollTop
}

const setScrollPosition = (
  container: Window | HTMLElement | null | undefined,
  position: number,
) => {
  if (!container) return
  if (container instanceof Window) {
    container.scrollTo(0, position)
    return
  }
  container.scrollTop = position
}

const getSavedScrollPositions = (): TrackedScrollPosition => {
  if (isServerSide) return {}

  try {
    const raw = sessionStorage.getItem(SCROLL_POSITIONS_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return {}
    return parsed as TrackedScrollPosition
  } catch {
    return {}
  }
}

const saveScrollPositions = (positions: TrackedScrollPosition) => {
  if (isServerSide) return

  try {
    sessionStorage.setItem(SCROLL_POSITIONS_KEY, JSON.stringify(positions))
  } catch {
    // Ignore storage errors (e.g. private mode/quota exceeded).
  }
}

export const useScrollRestoration = () => {
  const router = useRouter()
  const isHistoryNavigationRef = useRef(false)

  // Mark history navigation (browser back/forward and router.back()).
  useEffect(() => {
    if (isServerSide) return

    const handlePopState = () => {
      isHistoryNavigationRef.current = true
    }

    window.addEventListener('popstate', handlePopState)
    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [])

  useEffect(() => {
    if (isServerSide || !('scrollRestoration' in window.history)) return

    const previousScrollRestoration = window.history.scrollRestoration
    window.history.scrollRestoration = 'manual'

    return () => {
      window.history.scrollRestoration = previousScrollRestoration
    }
  }, [])

  useEffect(() => {
    const handleRouteChangeStart = () => {
      const scrollContainer = getScrollContainer()
      const scrollPosition = getScrollPosition(scrollContainer)
      const currentPath = router.asPath
      const prevPositions = getSavedScrollPositions()

      const newPositions = {
        ...prevPositions,
        [currentPath]: {
          position: scrollPosition,
          timestamp: Date.now(),
        },
      }

      const paths = Object.keys(newPositions)
      if (paths.length > MAX_TRACKED_PATHS) {
        const oldestPath = paths.reduce((oldestPath, path) => {
          if (
            newPositions[path].timestamp < newPositions[oldestPath].timestamp
          ) {
            return path
          }
          return oldestPath
        }, paths[0] as string)
        delete newPositions[oldestPath]
      }

      saveScrollPositions(newPositions)
    }

    const handleRouteChangeComplete = (url: string) => {
      const restoreTo = isHistoryNavigationRef.current
        ? getSavedScrollPositions()[url]?.position || 0
        : 0

      isHistoryNavigationRef.current = false

      // Wait one frame so the new route layout mounts before applying scroll.
      requestAnimationFrame(() => {
        setScrollPosition(getScrollContainer(), restoreTo)
      })
    }

    const handleRouteChangeError = () => {
      isHistoryNavigationRef.current = false
    }

    router.events.on('routeChangeStart', handleRouteChangeStart)
    router.events.on('routeChangeComplete', handleRouteChangeComplete)
    router.events.on('routeChangeError', handleRouteChangeError)

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart)
      router.events.off('routeChangeComplete', handleRouteChangeComplete)
      router.events.off('routeChangeError', handleRouteChangeError)
    }
  }, [router.asPath, router.events])
}
